import { DocumentType } from 'app/document/type';
import axios, { AxiosResponse } from 'axios';
import i18next from 'i18next';
import _ from 'lodash';
//@ts-ignore
import JSONbig from 'json-bigint';
import { Toaster } from './app/shared/component/toaster';
import { getBasePath } from './app/shared/utils/url-util';

import { WafError } from './app/shared/component/WafErrorModal/constant/type';
import { createWafModal } from './app/shared/component/WafErrorModal/action/action';

const apiUrl = getBasePath();

const instance = axios.create({
  baseURL: `${apiUrl}/maf-api`,
  headers: {
    Accept: 'application/json;charset=UTF-8',
  },
});

export function updateHeader(token: string) {
  if (instance.defaults.headers) {
    // @ts-ignore
    instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
}

function e(param: any) {
  return encodeURIComponent(param);
}

const api = {
  documents: {
    findPage: ({ entryFormId = '', page = 0, size = 3, filters = '', sorters = '' }) => {
      return instance.get(`/api/v1/entry-forms/${e(entryFormId)}/documents?page=${page}&size=${size}${filters}${sorters}`);
    },
    findOne: (entryFormId: string, id: string) => instance.get(`/api/v1/entry-forms/${e(entryFormId)}/documents/${e(id)}`),
    create: (entryFormId: string, document: FormData) => {
      const config = { headers: { 'content-type': 'multipart/form-data' } };
      return instance.post(`/api/v1/entry-forms/${e(entryFormId)}/documents`, document, config);
    },
    update: (entryFormId: string, id: string, document: any) =>
      instance.put(`/api/v1/entry-forms/${e(entryFormId)}/documents/${e(id)}`, document),
    delete: (entryFormId: string, id: string) => {
      return instance.delete(`/api/v1/entry-forms/${e(entryFormId)}/documents/${e(id)}`);
    },
    restore: (entryFormId: string, id: string) => {
      return instance.patch(`/api/v1/entry-forms/${e(entryFormId)}/documents/${e(id)}/restore`);
    },
    updateComment: (entryFormId: string, id: string, publicComment: string) =>
      instance.put(`/api/v1/entry-forms/${e(entryFormId)}/documents/${e(id)}/comment`, { publicComment }),
    download: (entryFormId: string, id: string) =>
      instance.get(`/api/v1/entry-forms/${e(entryFormId)}/documents/${e(id)}/download`, { responseType: 'blob' }),
  },
  entryForm: {
    fetchAll: ({ page = 0, size = 10, filters = '', sorters = '' }) =>
      instance.get(`/api/v1/entry-forms?page=${page}&size=${size}${filters}${sorters}`),
    get: (id: string) => instance.get(`/api/v1/entry-forms/${e(id)}`),
    create: (entryForm: any) => instance.post('/api/v1/entry-forms', entryForm),
    update: (id: string, entryForm: any) => instance.put(`/api/v1/entry-forms/${e(id)}`, entryForm),
    initFromPDF: (file: any) => instance.post('/api/v1/entry-forms/init', file),
    fetchRelatedRequests: ({ id, page = 0, size = 10, filters = '', sorters = '' }: any) =>
      instance.get(`/api/v1/entry-forms/${e(id)}/related-requests?page=${page}&size=${size}${filters}${sorters}`),
    submit: (id: string) => instance.post(`/api/v1/entry-forms/${e(id)}/submit`),
    delete: (id: string) => instance.delete(`/api/v1/entry-forms/${e(id)}`),
    withdraw: (id: string) => instance.post(`/api/v1/entry-forms/${e(id)}/withdraw`),
    submitNotifications: (id: string) => instance.post(`/api/v1/entry-forms/${e(id)}/submit-notifications`),
    submitNewDocuments: (id: string) => instance.post(`/api/v1/entry-forms/${e(id)}/submit-new-documents`),
    eugbr: {
      findPage: ({ entryFormId = '', page = 0, size = 3, filters = '', sorters = '' }) => {
        return instance.get(`/api/v1/entry-forms/${e(entryFormId)}/eugbr?page=${page}&size=${size}${filters}${sorters}`);
      },
      findPageDocuments: ({ entryFormId = '', page = 0, size = 3, filters = '', sorters = '' }) => {
        return instance.get(`/api/v1/entry-forms/${e(entryFormId)}/eugbr/documents?page=${page}&size=${size}${filters}${sorters}`);
      },
      create: (entryFormId: string, eugbr: any) => {
        return instance.post(`/api/v1/entry-forms/${e(entryFormId)}/eugbr`, eugbr);
      },
      delete: (entryFormId: string, eugbrId: string) => {
        return instance.delete(`/api/v1/entry-forms/${e(entryFormId)}/eugbr/${e(eugbrId)}`);
      },
      update: (entryFormId: string, eugbrId: string, eugbr: any) =>
        instance.post(`/api/v1/entry-forms/${e(entryFormId)}/eugbr/${e(eugbrId)}`, eugbr),
      submit: (entryFormId: string) => instance.post(`/api/v1/entry-forms/${e(entryFormId)}/eugbr/submit`),
    },
  },
  finalTerm: {
    fetchAll: ({ entryFormId = '', page = 0, size = 3, filters = '', sorters = '' }) => {
      return instance.get(`/api/v1/entry-forms/${e(entryFormId)}/final-terms?page=${page}&size=${size}${filters}${sorters}`);
    },
    get: (entryFormId: string, id: string) => instance.get(`/api/v1/entry-forms/${e(entryFormId)}/final-terms/${e(id)}`),
    update: (entryFormId: string, id: string, document: DocumentType) =>
      instance.put(`/api/v1/entry-forms/${e(entryFormId)}/final-terms/${e(id)}`, document),
    findLink: (entryFormId: string) => instance.get(`/api/v1/entry-forms/${e(entryFormId)}/final-terms/linked`),
    link: (entryFormId: string) => instance.get(`/api/v1/entry-forms/${e(entryFormId)}/final-terms/link`),
    unlink: (entryFormId: string, id: string) => instance.get(`/api/v1/entry-forms/${e(entryFormId)}/final-terms/unlink/${e(id)}`),
  },
  validationRule: {
    validate: (entryForm: any) => instance.post(`/api/v1/entry-forms/${e(entryForm.id)}/validation?locale=english`, entryForm),
  },
  technicalError: (code: string, url: string, method: string) => instance.post(`/api/v1/audit`, { code, url, method }),
};

const treatError = (error: any) => {
  if ((error.response.status >= 500 && error.response.status < 600) || error.response.status === 418 || error.response.status === 405) {
    Toaster.error(i18next.t('notifications.errors.serverError'));
  } else if (error.response.status === 400) {
    Toaster.error(i18next.t('notifications.errors.invalidParameters'));
  } else if (error.response.status === 401) {
    Toaster.error(i18next.t('notifications.errors.sessionExpired'));
  } else if (error.response.status === 403) {
    Toaster.error(i18next.t('notifications.errors.forbidden'));
  } else if (error.response.status === 404) {
    Toaster.error(i18next.t('notifications.errors.notFound'));
  } else if (error.response.status === 440 && !error.response.config.url.endsWith('/audit')) {
    const newWafError: WafError = {
      correlationId: _.get(error, 'response.data.correlationId', 'UNKNOWN'),
      url: `${error.response.config.baseURL}${error.response.config.url}`,
      method: error.response.config.method,
    };
    createWafModal(newWafError);
  } else if (error.response.status === 413) {
    Toaster.error(i18next.t('notifications.errors.payloadTooLarge'));
  }
  return Promise.reject(error);
};

instance.interceptors.response.use((response: AxiosResponse<any>) => {
  const contentType = String(response.headers['content-type'] || '');

  if (contentType?.includes('application/json')) {
    response.data = JSONbig.parse(JSON.stringify(response.data));
  }
  return response;
}, treatError);

export default api;
